import React, { useMemo } from "react";

/**
 * Reusable editor for InjDispWellInfo (H-10 / injection-disposal setup).
 *
 * The InjDispWellInfo table is a one-to-one extension of the `leases` table
 * keyed by LeaseID. This component edits a single row in-place via a
 * controlled `value` / `onChange` pattern that mirrors how LeasesTab handles
 * the parent lease object.
 *
 * Notes:
 *  - All fields are optional from the UI's perspective; the API only persists
 *    keys that are present in the payload (NULL-preserving).
 *  - For OIL/GAS leases this section is not shown at all (parent gates render).
 */
export default function InjDispWellInfoSection({ value, onChange, theme }) {
  const isDark = theme === "dark";
  const info = value || {};

  const inputClasses = isDark
    ? "w-full px-2 py-1 bg-gray-700 text-gray-100 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
    : "w-full px-2 py-1 bg-white text-gray-800 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500";

  const sectionClasses = `mb-3 p-3 rounded-md border ${
    isDark ? "border-gray-700 bg-gray-800/40" : "border-gray-200 bg-gray-50"
  }`;

  const set = (field, v) => onChange({ ...info, [field]: v });

  const num = (v) => {
    if (v === null || v === undefined || v === "") return "";
    return String(v);
  };

  const pctFields = [
    ["SWPct", "Salt Water"],
    ["NORMPct", "NORM"],
    ["FreshPct", "Fresh"],
    ["TotalCO2Pct", "Total CO₂"],
    ["AnthropogenicCO2Pct", "Anthropogenic CO₂"],
    ["NGPct", "Natural Gas"],
    ["H2SPct", "H₂S"],
    ["PolymerPct", "Polymer"],
    ["SteamPct", "Steam"],
    ["AirPct", "Air"],
    ["NitrogenPct", "Nitrogen"],
    ["FracWaterFBPct", "Frac Water FB"],
    ["OtherPct", "Other"],
  ];

  const pctSum = useMemo(() => {
    return pctFields.reduce((acc, [k]) => {
      const n = parseInt(info[k], 10);
      return acc + (isNaN(n) ? 0 : n);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    info.SWPct, info.NORMPct, info.FreshPct, info.TotalCO2Pct,
    info.AnthropogenicCO2Pct, info.NGPct, info.H2SPct, info.PolymerPct,
    info.SteamPct, info.AirPct, info.NitrogenPct, info.FracWaterFBPct,
    info.OtherPct,
  ]);

  const sumOk = pctSum === 0 || pctSum === 100;

  return (
    <div className="text-sm">
      <div className="mb-2 font-semibold flex items-center gap-2">
        <span>H-10 / Injection-Disposal Well Info</span>
        <span
          className={`text-xs px-2 py-0.5 rounded ${
            isDark
              ? "bg-gray-700 text-gray-300"
              : "bg-gray-200 text-gray-700"
          }`}
        >
          one-to-one with this lease
        </span>
      </div>

      {/* Identification + H-10 setup */}
      <div className={sectionClasses}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block mb-1 font-semibold">Well Number</label>
            <input
              type="text"
              maxLength={6}
              value={info.WellNumber ?? ""}
              onChange={(e) => set("WellNumber", e.target.value)}
              className={inputClasses}
              placeholder="e.g., 17W"
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold">H-10 API</label>
            <input
              type="text"
              maxLength={8}
              value={info.H10API ?? ""}
              onChange={(e) => set("H10API", e.target.value)}
              className={inputClasses}
              placeholder="8-digit API"
              inputMode="numeric"
              pattern="\d*"
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold">UIC Type</label>
            <select
              value={num(info.UICType)}
              onChange={(e) => set("UICType", e.target.value)}
              className={inputClasses}
            >
              <option value="">— select —</option>
              <option value="1">1 — Class I</option>
              <option value="2">2 — Class II EOR</option>
              <option value="3">3 — Class II SWD</option>
              <option value="5">5 — Class V</option>
            </select>
          </div>

          <div>
            <label className="block mb-1 font-semibold">H-10 Month Due</label>
            <select
              value={info.H10MonthDue ?? ""}
              onChange={(e) => set("H10MonthDue", e.target.value)}
              className={inputClasses}
            >
              <option value="">— select —</option>
              {[
                ["01", "January"],
                ["02", "February"],
                ["03", "March"],
                ["04", "April"],
                ["05", "May"],
                ["06", "June"],
                ["07", "July"],
                ["08", "August"],
                ["09", "September"],
                ["10", "October"],
                ["11", "November"],
                ["12", "December"],
              ].map(([v, label]) => (
                <option key={v} value={v}>
                  {v} — {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 font-semibold">Inject Through</label>
            <select
              value={num(info.InjThrough)}
              onChange={(e) => set("InjThrough", e.target.value)}
              className={inputClasses}
            >
              <option value="">— select —</option>
              <option value="1">1 — Tubing</option>
              <option value="2">2 — Casing</option>
            </select>
          </div>

          <div className="flex items-center pt-5">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={(info.InjFluidSourceOthers || "N") === "Y"}
                onChange={(e) =>
                  set("InjFluidSourceOthers", e.target.checked ? "Y" : "N")
                }
              />
              <span>Fluids from outside sources</span>
            </label>
          </div>
        </div>
      </div>

      {/* Depths */}
      <div className={sectionClasses}>
        <div className="mb-2 font-semibold">Depths (ft)</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block mb-1">Inj. Interval — Top</label>
            <input
              type="number"
              min="0"
              step="1"
              value={num(info.InjIntervalDepthTop)}
              onChange={(e) => set("InjIntervalDepthTop", e.target.value)}
              className={inputClasses}
              inputMode="numeric"
            />
          </div>
          <div>
            <label className="block mb-1">Inj. Interval — Bottom</label>
            <input
              type="number"
              min="0"
              step="1"
              value={num(info.InjIntervalDepthBottom)}
              onChange={(e) => set("InjIntervalDepthBottom", e.target.value)}
              className={inputClasses}
              inputMode="numeric"
            />
          </div>
          <div>
            <label className="block mb-1">Tubing/Packer Depth</label>
            <input
              type="number"
              min="0"
              step="1"
              value={num(info.TbgPackerDepth)}
              onChange={(e) => set("TbgPackerDepth", e.target.value)}
              className={inputClasses}
              inputMode="numeric"
            />
          </div>
        </div>
      </div>

      {/* Injected fluid composition */}
      <div className={sectionClasses}>
        <div className="mb-2 flex items-center justify-between">
          <div className="font-semibold">Injected Fluid Composition (%)</div>
          <div
            className={`text-xs px-2 py-0.5 rounded ${
              sumOk
                ? isDark
                  ? "bg-green-900/40 text-green-300"
                  : "bg-green-100 text-green-800"
                : isDark
                  ? "bg-yellow-900/40 text-yellow-300"
                  : "bg-yellow-100 text-yellow-800"
            }`}
            title="Sum should equal 100 when any composition is set."
          >
            sum: {pctSum}%
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {pctFields.map(([key, label]) => (
            <div key={key}>
              <label className="block mb-1">{label}</label>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={num(info[key])}
                onChange={(e) => set(key, e.target.value)}
                className={inputClasses}
                inputMode="numeric"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Other type codes + comment */}
      <div className={sectionClasses}>
        <div className="mb-2 font-semibold">Other Fluid Type Codes</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block mb-1">Other Type 1</label>
            <input
              type="number"
              min="0"
              step="1"
              value={num(info.OtherType1)}
              onChange={(e) => set("OtherType1", e.target.value)}
              className={inputClasses}
              inputMode="numeric"
            />
          </div>
          <div>
            <label className="block mb-1">Other Type 2</label>
            <input
              type="number"
              min="0"
              step="1"
              value={num(info.OtherType2)}
              onChange={(e) => set("OtherType2", e.target.value)}
              className={inputClasses}
              inputMode="numeric"
            />
          </div>
          <div>
            <label className="block mb-1">Other Type 3</label>
            <input
              type="number"
              min="0"
              step="1"
              value={num(info.OtherType3)}
              onChange={(e) => set("OtherType3", e.target.value)}
              className={inputClasses}
              inputMode="numeric"
              title="Use 99 if not pre-defined; describe in Other Comment."
            />
          </div>
          <div className="sm:col-span-3">
            <label className="block mb-1">Other Comment</label>
            <input
              type="text"
              maxLength={32}
              value={info.OtherComment ?? ""}
              onChange={(e) => set("OtherComment", e.target.value)}
              className={inputClasses}
              placeholder="Free-text (max 32 chars)"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
